import styled, { keyframes } from "styled-components";
import {
  brand,
  border,
  radius,
  spacing,
  status,
  surface,
  text,
  typography,
} from "../../theme/tokens";

/**
 * Attorney Inbox styled components (FE Phase 5a — read surface).
 *
 * The page mirrors the matter-workspace 3-pane layout (sidebar list /
 * Apryse viewer / AIPane right rail) but with inbox-specific chrome:
 *   - Left pane is a flat chronological list (no folder tree),
 *   - Middle pane wraps the same Apryse `WebViewer` instance pattern
 *     used by `MailIntake/AssignView` so the document switches without
 *     re-mounting the (expensive) WASM viewer,
 *   - Right rail hosts `<AIPane mode="inbox">` with an inbox-specific
 *     header above it (doc title + matter subtitle + "Open in matter"
 *     link).
 *
 * Token usage: spacing / radius / colors all flow from `theme/tokens.js`
 * so the inbox surface inherits any future palette changes for free. The
 * one bespoke color is the unread row dot — kept inline to match the
 * brand-blue accent the AI panel uses.
 */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(2px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const skeletonShimmer = keyframes`
  0%   { background-position: -180% 0; }
  100% { background-position: 180% 0; }
`;

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export const InboxPageWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  background: ${surface.page};
  padding: 10px 0 0 0;
  box-sizing: border-box;
  overflow: hidden;
`;

export const InboxLayout = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  height: calc(100vh - 20px);
  gap: 8px;
  box-sizing: border-box;
  overflow: hidden;
`;

// ---------------------------------------------------------------------------
// Column 1 — Inbox list pane
// ---------------------------------------------------------------------------

/**
 * Sizing wrapper for the list pane. Width is user-draggable (grab strip
 * on the right edge) and lives in `Inbox.js` via `useResizableWidth`;
 * the inner `InboxListPane` just fills it. Bounds are enforced in the
 * hook (`INBOX_LIST_MIN_WIDTH` / `INBOX_LIST_MAX_WIDTH` in `Inbox.js`).
 */
export const InboxListColumn = styled.div`
  position: relative;
  flex-shrink: 0;
  width: ${({ $width }) => `${$width}px`};
  height: 100%;
`;

/**
 * Thin invisible grab strip straddling the list's right edge. Tints
 * brand-blue on hover / while dragging so the affordance is discoverable
 * without adding permanent chrome.
 */
export const InboxColumnResizer = styled.button`
  position: absolute;
  top: 0;
  right: -6px;
  width: 12px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  border: none;
  padding: 0;
  z-index: 12;
  user-select: none;
  touch-action: none;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 4px;
    width: 4px;
    border-radius: 2px;
    background: transparent;
    transition: background 120ms ease;
  }

  &:hover::after,
  &:focus-visible::after,
  &[data-active="true"]::after {
    background: rgba(12, 83, 187, 0.35);
  }

  &:focus-visible {
    outline: none;
  }
`;

export const InboxListPane = styled.aside`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: 100%;
  background: ${surface.surface};
  border-top-left-radius: ${radius.lg};
  border-top-right-radius: ${radius.lg};
  overflow: hidden;
`;

export const InboxListHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding: ${spacing[4]} ${spacing[5]};
  border-bottom: 1px solid ${border.light};
`;

export const InboxListTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing[3]};
`;

export const InboxListTitle = styled.h1`
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  margin: 0;
  letter-spacing: -0.01em;
`;

export const InboxListCount = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  font-weight: ${typography.weight.medium};
  font-variant-numeric: tabular-nums;
`;

export const InboxFiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

export const InboxFolderWrap = styled.div`
  position: relative;
`;

export const InboxFolderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[1]} ${spacing[2.5]};
  border-radius: ${radius.pill};
  border: 1px solid ${({ $active }) => ($active ? brand.primary : border.default)};
  background: ${({ $active }) => ($active ? brand.primaryLight : "transparent")};
  color: ${({ $active }) => ($active ? brand.primary : text.secondary)};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  cursor: pointer;

  &:hover {
    background: ${brand.primaryLight};
    color: ${brand.primary};
    border-color: ${brand.primary};
  }
`;

export const InboxFolderCount = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: ${typography.weight.medium};
  color: ${text.muted};
`;

export const InboxFolderMenu = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  min-width: 240px;
  max-height: 320px;
  overflow-y: auto;
  padding: ${spacing[1]} 0;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  background: ${surface.surface};
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
`;

export const InboxFolderOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  width: 100%;
  padding: ${spacing[2]} ${spacing[3]};
  border: none;
  background: ${({ $active }) => ($active ? brand.primaryLight : "transparent")};
  color: ${text.primary};
  font-size: ${typography.size.sm};
  font-weight: ${({ $active }) =>
    $active ? typography.weight.semibold : typography.weight.medium};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${brand.primaryLight};
  }
`;

export const InboxSelectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[5]};
  border-bottom: 1px solid ${border.light};
  background: ${brand.primaryLight};
  color: ${brand.primary};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
`;

export const InboxSelectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

export const InboxSelectionButton = styled.button`
  border: none;
  background: none;
  color: ${brand.primary};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
`;

export const InboxMatterSearchWrap = styled.div`
  position: relative;
  width: 100%;
  margin-top: ${spacing[1]};

  /* The Matters-page placeholder is long; keep it inside this pane. */
  & > div:first-child {
    overflow: hidden;
  }
`;

export const InboxMatterSearchResults = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  margin: 0;
  padding: ${spacing[1]} 0;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.lg};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;

export const InboxMatterSearchResult = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: ${spacing[2]} ${spacing[3]};
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${brand.primaryLight};
  }

  &:focus-visible {
    outline: 2px solid ${brand.primary};
    outline-offset: -2px;
  }
`;

export const InboxMatterSearchResultName = styled.span`
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const InboxMatterSearchResultMeta = styled.span`
  font-size: ${typography.size.xs};
  color: ${text.tertiary};
`;

export const InboxMatterSearchEmpty = styled.li`
  padding: ${spacing[3]};
  text-align: center;
  color: ${text.muted};
  font-size: ${typography.size.sm};
`;

/**
 * Inbox filter chip — used for the "Show snoozed" toggle and any future
 * matter-id chip filter. Active state mirrors the AnalysisStatusPill
 * brand-blue treatment so the affordance is visually consistent with
 * the AI surface.
 */
export const InboxFilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[2.5]};
  border-radius: ${radius.pill};
  border: 1px solid ${({ $active }) => ($active ? brand.primary : border.default)};
  background: ${({ $active }) => ($active ? brand.primaryLight : "transparent")};
  color: ${({ $active }) => ($active ? brand.primary : text.secondary)};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;

  &:hover {
    background: ${brand.primaryLight};
    color: ${brand.primary};
    border-color: ${brand.primary};
  }

  &:focus-visible {
    outline: 2px solid ${brand.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
    background: transparent;
    color: ${text.secondary};
    border-color: ${border.default};
  }
`;

export const InboxListBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const InboxListEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  padding: ${spacing[8]} ${spacing[5]};
  color: ${text.tertiary};
  text-align: center;
  font-size: ${typography.size.md};
`;

export const InboxListEmptyTitle = styled.div`
  font-weight: ${typography.weight.semibold};
  color: ${text.secondary};
  font-size: ${typography.size.md};
`;

export const InboxListEmptyDescription = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  max-width: 240px;
  line-height: 1.5;
`;

export const InboxListErrorBanner = styled.div`
  margin: ${spacing[3]} ${spacing[4]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${radius.md};
  border: 1px solid #EDB1B0;
  background: #FCEDED;
  color: #A61412;
  font-size: ${typography.size.sm};
`;

// ---------------------------------------------------------------------------
// Inbox list row
// ---------------------------------------------------------------------------

export const InboxRow = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
  padding: ${spacing[3]} ${spacing[4]};
  border: none;
  border-bottom: 1px solid ${border.light};
  background: ${({ $selected, $checked }) =>
    $selected || $checked ? brand.primaryLight : "transparent"};
  cursor: pointer;
  gap: ${spacing[1.5]};
  position: relative;
  transition: background 120ms ease;
  animation: ${fadeIn} 200ms ease;

  &:hover {
    background: ${({ $selected, $checked }) =>
      $selected || $checked ? brand.primaryLight : surface.hover};
  }

  &:focus-visible {
    outline: 2px solid ${brand.primary};
    outline-offset: -2px;
  }

  /* Left bookmark — brand-blue stripe on the selected row mirrors the
     "active" treatment used elsewhere in the app (sidebar items). */
  ${({ $selected }) =>
    $selected &&
    `
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: ${brand.primary};
    }
  `}
`;

export const InboxRowTopLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  width: 100%;
`;

export const InboxRowUnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${brand.primary};
  flex-shrink: 0;
  /* Reserve space for the dot even when read so the title column
     doesn't shift left/right on toggle. */
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
`;

export const InboxRowTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${typography.size.md};
  font-weight: ${({ $unread }) =>
    $unread ? typography.weight.semibold : typography.weight.medium};
  color: ${text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const InboxRowTimestamp = styled.span`
  flex-shrink: 0;
  font-size: ${typography.size.xs};
  color: ${text.muted};
  font-variant-numeric: tabular-nums;
`;

export const InboxRowPreview = styled.p`
  margin: 0;
  font-size: ${typography.size.sm};
  color: ${text.tertiary};
  line-height: 1.45;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Bottom-line meta — small inline chips for context analysis status,
 * task counts, snooze badge. Each chip is a separate styled component
 * below; this row just lays them out.
 */
export const InboxRowMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

const META_CHIP_TONES = {
  context: { fg: brand.primary, bg: brand.primaryLight, border: "#B9D2F0" },
  task: { fg: "#B26B00", bg: "#FFF4D9", border: "#F2D38A" },
  message: { fg: "#48566B", bg: "#EEF1F6", border: "#DDE2E9" },
  snooze: { fg: "#48566B", bg: "#EEF1F6", border: "#DDE2E9" },
  // "You have seen this" — warmer than a task so it reads before the
  // eye reaches the other chips, but never alarming: the flag is
  // advisory and is sometimes wrong.
  duplicate: { fg: "#9A3412", bg: "#FFEDD5", border: "#FDBA74" },
  // The demoted form. Deliberately quiet — "looks related" should not
  // compete with a real duplicate for attention.
  duplicateWeak: { fg: "#5B5563", bg: "#F3F1F5", border: "#DDD8E3" },
  // "This is Chris's letter, you are covering his inbox" — informative,
  // not a call to action, so it sits in the same quiet register as the
  // message / snooze chips but with its own hue to be found at a glance.
  coveringFor: { fg: "#1F5F4A", bg: "#E6F4EE", border: "#B7DCCB" },
  // Context+ (2026-10-21) — the row's bucket. "Needs you" is the one
  // chip on the row that is allowed to be loud: a decision or proposal
  // with a clock on it that the attorney must read. "Routine" is the
  // paralegal's register (amber, like a task); "File only" is as quiet
  // as a snooze.
  needsYou: { fg: status.failed.fg, bg: status.failed.bg, border: status.failed.border },
  routine: { fg: status.review.fg, bg: status.review.bg, border: status.review.border },
  fileOnly: { fg: status.neutral.fg, bg: status.neutral.bg, border: status.neutral.border },
  // The clock: how long until the window on this letter closes. Red
  // inside a week, amber inside a month, otherwise quiet; a window that
  // has already run is brown — still worth a look, no longer urgent.
  clockSoon: { fg: status.failed.fg, bg: status.failed.bg, border: status.failed.border },
  clockNear: { fg: status.review.fg, bg: status.review.bg, border: status.review.border },
  clockLater: { fg: status.neutral.fg, bg: status.neutral.bg, border: status.neutral.border },
  clockClosed: { fg: status.closed.fg, bg: status.closed.bg, border: status.closed.border },
};

export const InboxRowChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 2px ${spacing[2]};
  border-radius: ${radius.pill};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  letter-spacing: 0.01em;
  background: ${({ $tone }) => (META_CHIP_TONES[$tone] || META_CHIP_TONES.message).bg};
  color: ${({ $tone }) => (META_CHIP_TONES[$tone] || META_CHIP_TONES.message).fg};
  border: 1px solid
    ${({ $tone }) => (META_CHIP_TONES[$tone] || META_CHIP_TONES.message).border};
  white-space: nowrap;
`;

/**
 * Context+ (2026-10-21): the one-line reason a letter sits where it does
 * in the priority order — "Proposed Reduction/Severance; 6 issues
 * denied". Rides in the meta row after the bucket and clock chips, in
 * the preview's register so it reads as a hint, not a headline.
 */
export const InboxRowTriageLine = styled.span`
  min-width: 0;
  font-size: ${typography.size.xs};
  color: ${text.tertiary};
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * The sort control: two chips that behave as one radio group
 * ("Priority" / "Newest"). A label in front so the row does not read
 * as more filters.
 */
export const InboxSortGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  margin-right: ${spacing[1]};
`;

export const InboxSortLabel = styled.span`
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  color: ${text.muted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: ${spacing[1]};
`;

// ---------------------------------------------------------------------------
// Skeleton row (used while the first page is loading)
// ---------------------------------------------------------------------------

export const InboxSkeletonRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${border.light};
  gap: ${spacing[2]};
`;

export const InboxSkeletonBlock = styled.div`
  height: ${({ $h }) => $h || 12}px;
  width: ${({ $w }) => $w || "100%"};
  border-radius: ${radius.sm};
  background: linear-gradient(
    90deg,
    ${border.light} 0%,
    ${surface.hover} 50%,
    ${border.light} 100%
  );
  background-size: 200% 100%;
  animation: ${skeletonShimmer} 1.4s linear infinite;
`;

// ---------------------------------------------------------------------------
// Pagination footer
// ---------------------------------------------------------------------------

export const InboxPaginationFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  border-top: 1px solid ${border.light};
  background: ${surface.surface};
  font-size: ${typography.size.sm};
  color: ${text.tertiary};
`;

export const InboxPaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[2.5]};
  border-radius: ${radius.md};
  border: 1px solid ${border.default};
  background: ${surface.surface};
  color: ${text.secondary};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: background 120ms ease;

  &:hover:not(:disabled) {
    background: ${surface.hover};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const InboxPaginationPageLabel = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[1.5]};
  border-radius: ${radius.sm};
  border: 1px dashed transparent;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;

  &:hover {
    background: ${surface.hover};
    border-color: ${border.default};
  }
`;

export const InboxPaginationPageForm = styled.form`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
`;

export const InboxPaginationPageInput = styled.input`
  width: 40px;
  padding: ${spacing[0.5]} ${spacing[1]};
  border-radius: ${radius.sm};
  border: 1px solid ${border.focus};
  text-align: center;
  font: inherit;
  color: ${text.primary};

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${brand.primaryLight};
  }

  /* Hide native number-input spinners so it reads as a plain page field. */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

// ---------------------------------------------------------------------------
// Column 2 — Document viewer pane
// ---------------------------------------------------------------------------

export const InboxViewerPane = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  background: ${surface.surface};
  border-top-left-radius: ${radius.lg};
  border-top-right-radius: ${radius.lg};
  overflow: hidden;

  /* The Apryse viewer is an iframe; while a column drag is in progress
     it must not receive the mouse, or the drag stalls the moment the
     cursor crosses into the document. The resize hook sets the body
     attribute for the duration of the drag only. */
  body[data-column-resizing="true"] & iframe {
    pointer-events: none;
  }
`;

export const InboxViewerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[5]};
  border-bottom: 1px solid ${border.light};
`;

export const InboxViewerDocName = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};

  > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const InboxViewerBody = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  background: #1f2937;
`;

export const InboxViewerEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: ${spacing[2]};
  padding: ${spacing[8]};
  color: ${text.tertiary};
  background: ${surface.page};
  text-align: center;
`;

export const InboxViewerEmptyTitle = styled.div`
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.semibold};
  color: ${text.secondary};
`;

export const InboxViewerEmptyDescription = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  max-width: 320px;
  line-height: 1.5;
`;

// ---------------------------------------------------------------------------
// Column 3 — AIPane right-rail wrapper
// ---------------------------------------------------------------------------

/**
 * Mirrors `MatterDetails`' AIPaneWrapper — same 500px width, same
 * always-on-top-corners radius — so the right rail looks identical
 * across surfaces. The display: flex/none toggle is driven by the
 * `?ai=...` URL state via `isAIPaneOpen`.
 */
export const INBOX_AI_RAIL_WIDTH = 500;
export const INBOX_AI_RAIL_COLLAPSED_WIDTH = 56;

const railWidthCss = ({ $collapsed }) =>
  `${$collapsed ? INBOX_AI_RAIL_COLLAPSED_WIDTH : INBOX_AI_RAIL_WIDTH}px`;

export const InboxAIPaneWrapper = styled.div`
  width: ${railWidthCss};
  min-width: ${railWidthCss};
  max-width: ${railWidthCss};
  height: 100%;
  background: ${surface.surface};
  border-top-left-radius: ${radius.lg};
  border-top-right-radius: ${radius.lg};
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

/**
 * Inbox-specific header that sits ABOVE the AIPane's existing
 * gradient strip / sparkle header. AIPane in matter mode doesn't
 * surface "what doc / what matter" because the URL + breadcrumb
 * already do that; in inbox mode we need to.
 */
export const InboxAIHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding: ${spacing[4]} ${spacing[4]} ${spacing[3]};
  border-bottom: 1px solid ${border.light};
  background: ${surface.surface};
`;

export const InboxAIHeaderTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]};
`;

export const InboxAIHeaderTitle = styled.h2`
  margin: 0;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  letter-spacing: -0.005em;
  line-height: 1.35;
  word-break: break-word;
`;

export const InboxAIHeaderSubtitle = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  font-size: ${typography.size.sm};
  color: ${text.tertiary};
  line-height: 1.4;

  .matter-name {
    color: ${text.secondary};
    font-weight: ${typography.weight.semibold};
  }

  .matter-number {
    color: ${text.muted};
    font-variant-numeric: tabular-nums;
  }
`;

/**
 * Own row under the matter name so Open/Closed + case manager sit at
 * the top-right without crowding the client line (Matthew, 2026-09-14:
 * the Brief panel may drop a line to make room).
 */
export const InboxAIHeaderMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing[2]};
  flex-wrap: wrap;

  .matter-case-manager {
    color: ${text.secondary};
    font-size: ${typography.size.sm};
    font-weight: ${typography.weight.semibold};
    white-space: nowrap;
  }
`;

const MATTER_STATUS_TONES = {
  open: status.ready,
  closed: status.closed,
  other: status.review,
};

export const InboxAIHeaderStatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px ${spacing[2]};
  border-radius: ${radius.pill};
  border: 1px solid
    ${({ $tone }) => (MATTER_STATUS_TONES[$tone] || MATTER_STATUS_TONES.other).border};
  background: ${({ $tone }) =>
    (MATTER_STATUS_TONES[$tone] || MATTER_STATUS_TONES.other).bg};
  color: ${({ $tone }) =>
    (MATTER_STATUS_TONES[$tone] || MATTER_STATUS_TONES.other).fg};
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  letter-spacing: 0.01em;
  line-height: 1.2;
  white-space: nowrap;
`;

export const InboxAIHeaderOpenInMatterLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  color: ${brand.primary};
  text-decoration: none;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${brand.primaryHover};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${brand.primary};
    outline-offset: 2px;
    border-radius: ${radius.sm};
  }
`;

export const InboxAIHeaderEmpty = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

// ---------------------------------------------------------------------------
// Proposed-deadline strip (deadline calendaring — BE dms_backend #1325)
// ---------------------------------------------------------------------------

/**
 * Sticky strip above the inbox rows listing proposed deadline cards.
 * Amber "review" tone: these are the human-confirmation gate for
 * statutory VA deadlines, so the strip should read as "needs a
 * decision", not as another unread row.
 */
export const DeadlineStripWrapper = styled.section`
  border-bottom: 1px solid ${border.light};
  background: #fffaf0;
  max-height: 42vh;
  overflow-y: auto;
  flex-shrink: 0;
`;

export const DeadlineStripHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[5]} ${spacing[1]};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.bold};
  color: #b26b00;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const DeadlineCardBox = styled.article`
  margin: ${spacing[2]} ${spacing[4]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${surface.surface};
  border: 1px solid #f0dcb4;
  border-left: 3px solid #b26b00;
  border-radius: ${radius.md};
  animation: ${fadeIn} 160ms ease;
`;

export const DeadlineCardTitle = styled.div`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  margin-bottom: ${spacing[1]};
`;

export const DeadlineCardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]} ${spacing[3]};
  font-size: ${typography.size.sm};
  color: ${text.secondary};
  margin-bottom: ${spacing[2]};

  strong {
    color: ${text.primary};
    font-variant-numeric: tabular-nums;
  }
`;

export const DeadlineCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

/**
 * Card action button. `$variant`:
 *   "primary" → [Create event] (brand blue, filled)
 *   "ghost"   → [View document] (outline)
 *   "danger"  → [Dismiss] (red text outline)
 */
export const DeadlineActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[3]};
  border-radius: ${radius.md};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border 120ms ease;

  ${({ $variant }) =>
    $variant === "primary"
      ? `
        background: ${brand.primary};
        border: 1px solid ${brand.primary};
        color: ${text.onAccent};
        &:hover:not(:disabled) { background: ${brand.primaryHover}; }
      `
      : $variant === "danger"
        ? `
        background: transparent;
        border: 1px solid ${border.default};
        color: #a61412;
        &:hover:not(:disabled) { border-color: #a61412; background: #fdf3f3; }
      `
        : `
        background: transparent;
        border: 1px solid ${border.default};
        color: ${text.secondary};
        &:hover:not(:disabled) { border-color: ${brand.primary}; color: ${brand.primary}; }
      `}

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const DeadlineStripError = styled.div`
  margin: ${spacing[2]} ${spacing[4]};
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${radius.md};
  background: #fdf3f3;
  border: 1px solid #f2c5c4;
  color: #a61412;
  font-size: ${typography.size.sm};
`;

/**
 * Full-tab incarnation of the deadline queue (Mail Intake → Deadlines).
 * The strip wrapper capped height at 42vh for its old above-the-inbox-
 * rows placement; as a tab root it must fill the ViewContainer like
 * its sibling tab views (Bugbot #308).
 */
export const DeadlineTabWrapper = styled(DeadlineStripWrapper)`
  max-height: none;
  height: 100%;
  flex: 1 1 auto;
  border-bottom: none;
`;

// ---------------------------------------------------------------------------
// Duplicate-document banner (right rail, above the AIPane)
// ---------------------------------------------------------------------------

// Advisory, never alarming. A strong flag is warm enough to notice at a
// glance; a weak one is deliberately grey so "looks related" cannot be
// mistaken for "you have seen this". Mirrors the fee-notice queue's
// WarningBanner posture, which took the same decision.
const DUPLICATE_BANNER_TONES = {
  strong: { fg: "#9A3412", bg: "#FFF7ED", border: "#FDBA74" },
  weak: { fg: "#5B5563", bg: "#F7F6F9", border: "#DDD8E3" },
  dismissed: { fg: text.muted, bg: surface.hover, border: border.light },
};

const duplicateTone = ($tone) =>
  DUPLICATE_BANNER_TONES[$tone] || DUPLICATE_BANNER_TONES.weak;

export const DuplicateBanner = styled.div`
  margin: 0 ${spacing[4]} ${spacing[3]};
  padding: ${spacing[3]};
  border-radius: ${radius.md};
  background: ${({ $tone }) => duplicateTone($tone).bg};
  border: 1px solid ${({ $tone }) => duplicateTone($tone).border};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const DuplicateBannerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
`;

export const DuplicateBannerLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ $tone }) => duplicateTone($tone).fg};
`;

export const DuplicateBannerOther = styled.div`
  font-size: ${typography.size.xs};
  color: ${text.primary};
  line-height: 1.45;
  word-break: break-word;

  strong {
    font-weight: ${typography.weight.semibold};
  }
`;

export const DuplicateBannerBody = styled.div`
  font-size: ${typography.size.xs};
  color: ${text.secondary};
  line-height: 1.45;
`;

export const DuplicateBannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  margin-top: 2px;
`;

export const DuplicateBannerLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${typography.size.xs};
  font-weight: ${typography.weight.semibold};
  color: ${({ $primary }) => ($primary ? brand.primary : text.secondary)};
  cursor: pointer;
  text-decoration: underline;

  &:hover:not(:disabled) {
    color: ${brand.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
